import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Eye, MapPin, ArrowLeft, Share2, Facebook, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Layout from "@/components/layout/Layout";
import { NewsCard } from "@/components/shared/NewsCard";
import { AdBanner, AdPlaceholder } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";
import { getImageUrl } from "@/utils/imageUrl";

export default function NoticiaDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/posts/slug/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error("Error fetching post:", error);
      } finally {
        setLoading(false);
      }
    };
    if (slug) fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-HN", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const typeLabels = {
    news: { label: "Noticia", backUrl: "/noticias", color: "bg-blue-100 text-blue-800" },
    destination: { label: "Destino", backUrl: "/destinos", color: "bg-green-100 text-green-800" },
    experience: { label: "Experiencia", backUrl: "/experiencias", color: "bg-purple-100 text-purple-800" },
    flavor: { label: "Sabor", backUrl: "/sabores", color: "bg-orange-100 text-orange-800" },
    video: { label: "Video", backUrl: "/videos", color: "bg-red-100 text-red-800" },
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post?.title || "")}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${post?.title} - ${shareUrl}`)}`,
    };
    window.open(urls[platform], "_blank", "width=600,height=400");
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="h-8 w-32 bg-gray-200 rounded mb-8" />
            <div className="h-12 w-3/4 bg-gray-200 rounded mb-4" />
            <div className="h-6 w-1/2 bg-gray-200 rounded mb-8" />
            <div className="aspect-video bg-gray-200 rounded-3xl mb-8" />
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 bg-gray-200 rounded" />
              <div className="h-4 w-3/4 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!post) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-600 mb-4">
            Contenido no encontrado
          </h1>
          <p className="text-gray-500 mb-8">
            El artículo que buscas no existe o ha sido eliminado.
          </p>
          <Link to="/noticias">
            <Button className="rounded-full">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a Noticias
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const typeInfo = typeLabels[post.post_type] || typeLabels.news;
  const placeholderImage = "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=85";

  return (
    <Layout>
      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="post-detail">
        {/* Back Link */}
        <Link
          to={typeInfo.backUrl}
          className="inline-flex items-center text-brand-textSecondary hover:text-primary transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Volver a {typeInfo.label}s
        </Link>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Badge className={`${typeInfo.color} border-0`}>{typeInfo.label}</Badge>
          {post.location_scope && (
            <Badge variant="secondary" className="bg-gray-100">
              {post.location_scope === "choluteca" ? "Choluteca" : post.location_scope === "honduras" ? "Honduras" : "Ambos"}
            </Badge>
          )}
        </div>

        {/* Title */}
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-brand-text leading-tight mb-6">
          {post.title}
        </h1>

        {/* Info Bar */}
        <div className="flex flex-wrap items-center gap-4 text-brand-textSecondary text-sm mb-8 pb-8 border-b border-gray-200">
          {post.author_name && (
            <span className="font-medium text-brand-text">
              Por {post.author_name}
            </span>
          )}
          {post.published_at && (
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {formatDate(post.published_at)}
            </span>
          )}
          {(post.municipio || post.departamento) && (
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {[post.municipio, post.departamento].filter(Boolean).join(", ")}
            </span>
          )}
          {post.view_count > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              {post.view_count} vistas
            </span>
          )}
        </div>

        {/* Featured Image */}
        {(post.featured_image_url || post.video_url) && (
          <div className="mb-10">
            {post.post_type === "video" && post.video_url ? (
              <div className="aspect-video rounded-3xl overflow-hidden bg-black">
                {post.video_url.includes("youtube.com") || post.video_url.includes("youtu.be") ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${post.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1]}`}
                    className="w-full h-full"
                    allowFullScreen
                    title={post.title}
                  />
                ) : (
                  <video
                    src={post.video_url}
                    controls
                    className="w-full h-full"
                  />
                )}
              </div>
            ) : (
              <img
                src={getImageUrl(post.featured_image_url) || placeholderImage}
                alt={post.title}
                className="w-full aspect-video object-cover rounded-3xl"
              />
            )}
          </div>
        )}

        {/* Gallery Images */}
        {post.gallery_images && post.gallery_images.length > 0 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4">Galería</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {post.gallery_images.map((img, index) => (
                <img
                  key={index}
                  src={getImageUrl(img)}
                  alt={`${post.title} - Imagen ${index + 1}`}
                  className="w-full aspect-square object-cover rounded-xl"
                />
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div
          className="prose-content max-w-none"
          dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          data-testid="post-content"
        />

        {/* Share */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-2 text-brand-textSecondary">
              <Share2 className="h-5 w-5" />
              Compartir:
            </span>
            <button
              onClick={() => handleShare("facebook")}
              className="share-btn facebook"
              aria-label="Compartir en Facebook"
              data-testid="share-facebook"
            >
              <Facebook className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleShare("twitter")}
              className="share-btn twitter"
              aria-label="Compartir en Twitter"
              data-testid="share-twitter"
            >
              <Twitter className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleShare("whatsapp")}
              className="share-btn whatsapp"
              aria-label="Compartir en WhatsApp"
              data-testid="share-whatsapp"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </button>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {post.related && post.related.length > 0 && (
        <section className="bg-white py-16" data-testid="related-posts">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl font-bold text-brand-text mb-8">
              Artículos Relacionados
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {post.related.map((relatedPost) => (
                <NewsCard key={relatedPost.post_id} post={relatedPost} className="h-[280px]" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ad Banner */}
      <section className="py-8 bg-gray-50">
        <AdBanner />
      </section>
    </Layout>
  );
}
